import { useState, useEffect, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Button,
  Box,
  Container,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DJSimpleTextBlock } from "../main/components/codeblock";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IProgress,
  ISessionProps,
  SessionStatus,
} from "../main/components/IProgress";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { Typography, Note, Select } from "@appsflyer/fe-ui-core";
import transition from "../main/components/transition";
import AdRevenueParamForm, {
  IAdRevenueParam,
  IOption,
} from "./AdRevenueParamForm";
import {
  amplitudeTrack_continue,
  amplitudeTrack_inputChange,
  InputType,
  interpolateSnippet,
} from "../services/utils";
import codeSnippets from "./codeSnippets.json";
import paramSnippets from "./paramSnippets.json";
import paramValueOverrides from "./paramValueOverrides.json";
import AmplitudeLink from "../services/AmplitudeLink";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

const AdRevenueSDK = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [value, setValue] = useState<IOption | null>(null);
  const [isDisabled, setIsDisabled] = useState(false); // Initially false
  const os = location.state?.os;
  const primaryLang = os === "android" ? "java" : "swift";
  const secondaryLang = os === "android" ? "kotlin" : "objectiveC";
  const [selectedCodeSnippetPrimary, setSelectedCodeSnippetPrimary] =
    useState("");
  const [selectedCodeSnippetSecondary, setSelectedCodeSnippetSecondary] =
    useState("");
  const [parameters, setParameters] = useState<IAdRevenueParam[]>([
    { type: "predefined", value: "AD_UNIT" },
    { type: "predefined", value: "AD_TYPE" },
    { type: "predefined", value: "PLACEMENT" },
  ]);
  const [logLineSnippet, setLogLineSnippet] = useState("");
  const [generatedCodePrimary, setGeneratedCodePrimary] = useState("");
  const [generatedCodeSecondary, setGeneratedCodeSecondary] = useState("");
  const [finalCombinedCodePrimary, setFinalCombinedCodePrimary] = useState("");
  const [finalCombinedCodeSecondary, setFinalCombinedCodeSecondary] =
    useState("");
  const lastSelectedMediationRef = useRef<string | null>(null);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log("Copied to clipboard:", text);
    });
  };

  const handleSelectChange = (selectedValue: IOption | null) => {
    setValue(selectedValue);
    setIsDisabled(true);
    amplitudeTrack_inputChange(
      location.state,
      InputType.Select,
      "mediation_network",
      selectedValue
    );
  };

  useEffect(() => {
    let mediationKey = "";

    if (value && typeof value === "object" && "value" in value) {
      mediationKey = value.value;
    } else if (typeof value === "string") {
      mediationKey = value;
    }

    // If mediation didn’t change, do nothing
    if (lastSelectedMediationRef.current === mediationKey) return;

    // Update reference so we know this is the last selected one
    lastSelectedMediationRef.current = mediationKey;

    const snippets = codeSnippets[mediationKey] || codeSnippets["generic"];

    if (!snippets) return;

    console.log("os", os);

    setSelectedCodeSnippetPrimary(
      interpolateSnippet(snippets[primaryLang], {
        MEDIATION_NETWORK: mediationKey.toUpperCase(),
      })
    );

    setSelectedCodeSnippetSecondary(
      interpolateSnippet(snippets[secondaryLang], {
        MEDIATION_NETWORK: mediationKey.toUpperCase(),
      })
    );

    const logLineMap = codeSnippets.logLine || {};
    const selectedLogLine =
      logLineMap[mediationKey]?.[primaryLang] ||
      logLineMap["generic"]?.[primaryLang] ||
      "";
    setLogLineSnippet(selectedLogLine);

    // Parameter logic — only update if mediation changed
    const overridesForMediation =
      paramValueOverrides[mediationKey]?.[primaryLang];

    if (
      overridesForMediation &&
      Object.keys(overridesForMediation).length > 0 &&
      Object.keys(overridesForMediation).length <= 3
    ) {
      const overriddenParams = Object.keys(
        overridesForMediation
      ).map<IAdRevenueParam>((key) => ({
        type: "predefined",
        value: key,
      }));
      setParameters(overriddenParams);
    } else {
      // fallback to default
      setParameters([
        { type: "predefined", value: "AD_UNIT" },
        { type: "predefined", value: "AD_TYPE" },
        { type: "predefined", value: "PLACEMENT" },
      ]);
    }
  }, [value, location.state?.os]);

  const predefinedOptions: IOption[] = [
    { value: "country", label: "COUNTRY" },
    { value: "ad_unit", label: "AD_UNIT" },
    { value: "ad_type", label: "AD_TYPE" },
    { value: "placement", label: "PLACEMENT" },
  ];

  useEffect(() => {
    const os = location.state?.os || "";
    const primaryLang = os === "android" ? "java" : "swift";
    const secondaryLang = os === "android" ? "kotlin" : "objectiveC";

    const mediationKey =
      ((value && typeof value === "object" && "value" in value
        ? value.value
        : value) as string) || "generic";

    const allOverrides = paramValueOverrides[mediationKey] || {};
    const overrides = allOverrides[primaryLang] || {};

    const buildCode = (lang: keyof typeof paramSnippets) => {
      const { header, line_predefined, line_custom } = paramSnippets[lang];

      const toSwiftParamName = (param: string) =>
        param
          .toLowerCase()
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join("");

      const lines = parameters
        .map((param) => {
          const paramRaw =
            param?.value && typeof param.value === "object"
              ? (param.value as IOption)?.value
              : param?.value;

          // Skip if no param value yet (for both predefined and custom)
          if (!paramRaw || paramRaw.trim() === "") {
            return null;
          }
          const paramUpper = paramRaw.toUpperCase();
          const overrideValue = overrides[paramUpper]; // still uses uppercase
          const valueSnippet = overrideValue || "<<VALUE>>";

          const lineTemplate =
            param.type === "predefined" ? line_predefined : line_custom;

          // Custom param name logic for Swift
          const finalParam =
            param.type === "predefined"
              ? lang === "swift"
                ? toSwiftParamName(paramUpper)
                : paramUpper
              : paramRaw;

          let fullLine = interpolateSnippet(lineTemplate, {
            PARAM: finalParam,
          });

          fullLine = fullLine.replace("<<VALUE>>", valueSnippet);

          // Add inline comment if value wasn't overridden and param is one of the known ones
          const shouldAddComment =
            !overrideValue && param.type === "predefined";
          const paramComments: Record<string, string> = {
            AD_TYPE: "// Type of the ad format, e.g. interstitial, banner",
            AD_UNIT: "// Ad unit from which the revenue was generated",
            PLACEMENT:
              "// Ad placement name where the user generated impression",
          };

          if (shouldAddComment) {
            const comment = paramComments[paramUpper];
            if (comment) {
              fullLine += ` ${comment}`;
            }
          }

          return fullLine;
        })
        .filter(Boolean); // remove null entries

      return [header, ...lines].join("\n");
    };

    setGeneratedCodePrimary(buildCode(primaryLang));
    setGeneratedCodeSecondary(buildCode(secondaryLang));
  }, [parameters, value, location.state?.os]);

  useEffect(() => {
    const combineSnippets = (
      base: string,
      paramCode: string,
      logLine: string
    ): string => {
      const baseLines = base.split("\n");

      // 1. Find the line where the constructor starts
      const constructorStartIndex = baseLines.findIndex(
        (line) =>
          line.includes("AFAdRevenueData adRevenueData") ||
          line.includes("= AFAdRevenueData(")
      );

      if (constructorStartIndex === -1) return base;

      // 2. Find the constructor's closing line (the one with `);`)
      let constructorEndIndex = constructorStartIndex;
      while (
        constructorEndIndex < baseLines.length &&
        !baseLines[constructorEndIndex].trim().endsWith(");") &&
        !baseLines[constructorEndIndex].trim().endsWith(")")
      ) {
        constructorEndIndex++;
      }

      if (constructorEndIndex >= baseLines.length) return base;

      // 3. Use indent from the constructor block
      const indent = baseLines[constructorEndIndex].match(/^\s*/)?.[0] || "";

      const paramLines = paramCode
        .split("\n")
        .filter((line) => line.trim()) // skip blank lines
        .map((line) => indent + line)
        .join("\n");

      const logLineIndented = indent + logLine;

      // 4. Inject param + log line *after* the constructor ends
      const updatedLines = [
        ...baseLines.slice(0, constructorEndIndex + 1), // include full constructor
        "", // blank line
        paramLines,
        "",
        logLineIndented,
        ...baseLines.slice(constructorEndIndex + 1), // rest of the code
      ];

      return updatedLines.join("\n");
    };

    setFinalCombinedCodePrimary(
      combineSnippets(
        selectedCodeSnippetPrimary,
        generatedCodePrimary,
        logLineSnippet
      )
    );
    setFinalCombinedCodeSecondary(
      combineSnippets(
        selectedCodeSnippetSecondary,
        generatedCodeSecondary,
        logLineSnippet
      )
    );
  }, [selectedCodeSnippetPrimary, generatedCodePrimary, logLineSnippet]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const buildEventValueString = () => {
    const mediationKey =
      ((value && typeof value === "object" && "value" in value
        ? value.value
        : value) as string) || "generic";

    const mediationNetwork = mediationKey.toLowerCase();
    const isAndroid = os === "android";

    // Try extracting monetization network
    const monetizationRegex = isAndroid
      ? /AFAdRevenueData\s*\(\s*("[^"]*"|[^,]*),/
      : /mediationNetwork:\s*"([^"]+)"/;
    const match = selectedCodeSnippetPrimary.match(monetizationRegex);
    let monetizationVal = "";
    if (match) {
      monetizationVal = match[1].replace(/^"|"$/g, "");
      if (monetizationVal.includes(".")) {
        monetizationVal = "";
      }
    }

    // Extract currency
    const currencyRegex = isAndroid
      ? /,\s*"([A-Z]{3})"\s*,/
      : /currency:\s*"([A-Z]{3})"/;
    const currencyMatch = selectedCodeSnippetPrimary.match(currencyRegex);
    const currency = currencyMatch ? currencyMatch[1] : "";

    // Extract revenue
    const revenueRegex = isAndroid
      ? /,\s*(\d+(\.\d+)?)\s*\)?\s*;/
      : /revenue:\s*([\d.]+)/;
    const revenueMatch = selectedCodeSnippetPrimary.match(revenueRegex);
    const revenue = revenueMatch ? revenueMatch[1] : "";

    const predefinedKeys = ["ad_unit", "ad_type", "placement", "country"];
    const flatParams: string[] = [];
    const customParams: string[] = [];

    parameters.forEach((param) => {
      const rawVal =
        typeof param.value === "object"
          ? (param.value as IOption)?.value
          : param.value;

      if (!rawVal) return;

      const key = rawVal.toLowerCase();
      if (param.type === "predefined" && predefinedKeys.includes(key)) {
        flatParams.push(`"${key}":"<<VALUE>>"`);
      } else if (key) {
        customParams.push(`"${key}":""`);
      }
    });

    let result = `{`;
    result += `"monetization_network":"${monetizationVal}",`;
    result += `"mediation_network":"${mediationNetwork}",`;
    result += `"event_revenue_currency":${currency ? `"${currency}"` : `"YOUR_REVENUE_CURRENCY"`},`;
    result += `"event_revenue":${revenue || `"YOUR_REVENUE"`}`;

    if (flatParams.length) {
      result += `,${flatParams.join(",")}`;
    }

    if (customParams.length) {
      result += `,"custom_parameters":{${customParams.join(",")}}`;
    }

    result += `}`;

    // Return it exactly as expected, with escaped quotes, no extra wrapping
    return result.replace(/"/g, '\\"');
  };

  const handleClick = () => {
    const progressArr: IProgress[] = location.state?.progress.slice();
    progressArr.push({
      id: "ad_revenue_choose_test",
      text: "progress_adrevenue_choose_test",
      order: 3,
    });

    const event_value = buildEventValueString();
    let eventParams: object = {};

    try {
      eventParams = JSON.parse(event_value.replace(/\\"/g, '"'));
    } catch (err) {
      console.error("Failed to parse event_value back into object:", err);
      eventParams = {}; // fallback to avoid crashing TestConsole_Results
    }

    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      sessionStatus: SessionStatus.ImplementedNotTested,
      currentPage: location.state?.currentPage + 1,
      eventParams,
    };

    amplitudeTrack_continue(location.state);

    console.log("Navigating with progressState:", progressState);

    navigate(`/adrevenue/${location.state.os}/testsdktype`, {
      state: {
        ...progressState,
        originalEventParams: eventParams, // <- save original for retry use
        originalEventValue: event_value, // <- optional, for full fidelity
      },
    });
  };

  // Mediation network options
  const mediationOptions = [
    { value: "admost", label: "Admost" },
    { value: "applovinmax", label: "Applovin Max" },
    { value: "appodeal", label: "Appodeal" },
    { value: "chartboost", label: "Chartboost" },
    { value: "custom_mediation", label: "Custom Mediation" },
    { value: "fyber", label: "Fyber" },
    { value: "googleadmob", label: "Google Admob" },
    {
      value: "ironsource",
      label: "IronSource (Unity) LevelPlay",
    },
    { value: "topon", label: "Topon" },
    { value: "toponpte", label: "Topon Pte" },
    { value: "tradplus", label: "Tradplus" },
    { value: "unity_ads_mediation", label: "Unity Ads Mediation" },
    { value: "yandex", label: "Yandex" },
  ];

  const steps = [
    {
      label: t("adRevenue_configure_mediation"),
      component: (
        <>
          <Typography mt={1} fontWeight={400}>
            {t("adRevenue_choose_mediation")}
          </Typography>
          <Typography my={1} fontWeight={"bold"}>
            {t("adRevenue_use_this_value")}
          </Typography>
          <Select
            options={mediationOptions}
            value={value}
            onChange={handleSelectChange}
          />
          {value && (
            <>
              {(() => {
                const mediationKey =
                  typeof value === "object" && "value" in value
                    ? value.value
                    : value;

                const isAndroidAdmob =
                  os === "android" && mediationKey === "googleadmob";

                return (
                  <>
                    {isAndroidAdmob && (
                      <Note title={""} sx={{ mt: 2, width: "700px" }}>
                        <Trans i18nKey="adRevenue_android_admob_note_body" />
                      </Note>
                    )}

                    <Alert severity="warning" sx={{ my: 2, width: "700px" }}>
                      <Trans i18nKey="adRevenue_generic_note_body" />
                    </Alert>
                  </>
                );
              })()}
              <Box position="relative" width={"700px"}>
                {/* Copy Button for Right Side (New Content) */}
                <Box
                  position="absolute"
                  top="4px" // Position at the top of the right side
                  left="675px" // Align to the right
                  style={{ color: "white" }}
                >
                  <Tooltip placement="top" title="Copy">
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Small outlined icon button"
                      onClick={() =>
                        copyToClipboard(selectedCodeSnippetPrimary)
                      }
                      style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
                    >
                      <ContentCopyRoundedIcon
                        fontSize="small"
                        style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
                <DJSimpleTextBlock
                  codePrimary={selectedCodeSnippetPrimary}
                  codeSecondary={selectedCodeSnippetSecondary}
                  showLineNumbers={true}
                  language={primaryLang}
                />
              </Box>
            </>
          )}
        </>
      ),
      shouldDisplayNavigationButtons: isDisabled,
    },
    {
      label: t("adRevenue_parameters"),
      component: (
        <>
          <AdRevenueParamForm
            parameters={parameters}
            onParamChange={setParameters}
            availablePredefinedOptions={predefinedOptions}
          />

          {/* Display the dynamically updated code block */}
          <Box mt={1.5}>
            <Box position="relative" width={"700px"}>
              {/* Copy Button for Right Side (New Content) */}
              <Box
                position="absolute"
                top="4px" // Position at the top of the right side
                left="675px" // Align to the right
                zIndex={10}
                style={{ color: "white" }}
              >
                <Tooltip placement="top" title="Copy">
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Small outlined icon button"
                    onClick={() => copyToClipboard(generatedCodePrimary)}
                    style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
                  >
                    <ContentCopyRoundedIcon
                      fontSize="small"
                      style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <DJSimpleTextBlock
                codePrimary={generatedCodePrimary}
                codeSecondary={generatedCodeSecondary}
                showLineNumbers={true}
                language={primaryLang}
              />
            </Box>
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("adRevenue_log_ad_revenue"),
      component: (
        <>
          <Typography variant="body2" weight="bold" mb={1}>
            {t("adRevenue_use_log_function")}
          </Typography>
          <Box position="relative" width={"700px"}>
            {/* Copy Button for Right Side (New Content) */}
            <Box
              position="absolute"
              top="4px" // Position at the top of the right side
              left="675px" // Align to the right
              zIndex={10}
              style={{ color: "white" }}
            >
              <Tooltip placement="top" title="Copy">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Small outlined icon button"
                  onClick={() => copyToClipboard(logLineSnippet)}
                  style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
                >
                  <ContentCopyRoundedIcon
                    fontSize="small"
                    style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <DJSimpleTextBlock
              codePrimary={logLineSnippet}
              language={primaryLang}
              showLineNumbers={false}
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("adRevenue_final_integration_snippet"),
      component: (
        <>
          <Typography variant="body2" weight="bold" mb={1}>
            {t("adRevenue_full_integration_code")}
          </Typography>
          <Box position="relative" width={"700px"}>
            {/* Copy Button for Right Side (New Content) */}
            <Box
              position="absolute"
              top="4px" // Position at the top of the right side
              left="675px" // Align to the right
              zIndex={10}
              style={{ color: "white" }}
            >
              <Tooltip placement="top" title="Copy">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Small outlined icon button"
                  onClick={() => copyToClipboard(finalCombinedCodePrimary)}
                  style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
                >
                  <ContentCopyRoundedIcon
                    fontSize="small"
                    style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <DJSimpleTextBlock
              codePrimary={finalCombinedCodePrimary}
              codeSecondary={finalCombinedCodeSecondary}
              showLineNumbers={true}
              language={primaryLang}
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Typography variant="h1">{t("adRevenue_title")}</Typography>
        <Note title={""} sx={{ mt: "12px" }}>
          <strong>{t("adRevenue_requirements")}</strong>
          <li
            style={{
              display: "list-item",
              textIndent: "-20px",
              paddingLeft: "20px",
            }}
          >
            <Trans i18nKey="adRevenue_requirements_1" />
          </li>
          <li
            style={{
              display: "list-item",
              textIndent: "-20px",
              paddingLeft: "20px",
            }}
          >
            <Trans i18nKey="adRevenue_requirements_2" />
          </li>
          <li
            style={{
              display: "list-item",
              textIndent: "-20px",
              paddingLeft: "20px",
            }}
          >
            <Trans i18nKey="adRevenue_requirements_3" />
            <br />
            <AmplitudeLink
              href="https://support.appsflyer.com/hc/en-us/articles/217490046-ROI360-ad-revenue-attribution-guide#connect-to-ad-revenue-integrated-partners"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("adRevenue_see_instructions")}{" "}
            </AmplitudeLink>
          </li>
        </Note>
        <Stepper
          activeStep={activeStep}
          style={{ border: "none" }}
          orientation="vertical"
        >
          {steps.map(({ label, component, shouldDisplayNavigationButtons }) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {component}
                {shouldDisplayNavigationButtons && (
                  <Box sx={{ mt: 2 }}>
                    {activeStep !== 0 && (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleBack}
                        // sx={{ mt: 2, mr: 1 }}
                      >
                        {t("general_Back")}
                      </Button>
                    )}
                    {activeStep !== steps.length - 1 && (
                      <Button
                        // sx={{ mt: 2 }}
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        {activeStep === steps.length - 1
                          ? t("general_Finish")
                          : t("general_Next")}
                      </Button>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={() => handleClick()}
          disabled={activeStep !== steps.length - 1}
        >
          {t("general_Continue")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(AdRevenueSDK);
